import { formatDistanceToNow, parseISO } from 'date-fns';
import { AlertCircle, ChevronDown, ChevronRight, Clock, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  ENTITY_SYNC_TYPES,
  useEntitySyncFieldConfigQuery,
  useEntitySyncListDataSuggestionsQuery,
  useEntitySyncListPairsQuery,
} from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  EntityPairSchemaRead,
  FilevineEntitySyncSuggestionSetSchema,
} from '@/services/api/generated/entitySyncService';

import { EntityLinkingSection } from '@/components/cms-sync/entity-linking';
import { FullPipelineButton } from '@/components/cms-sync/FullPipelineButton';
import type { CanonicalSectionType } from '@/components/cms-sync/entity-linking/finch-cards';
import { PollingProvider } from '@/components/cms-sync/entity-linking/context/PollingContext';
import { SyncSuggestionCardList } from '@/components/cms-sync/entity-linking/sync-suggestions';

const PANELS: Array<{ entityType: CanonicalSectionType; label: string }> = [
  { entityType: ENTITY_SYNC_TYPES.MEDICAL_PROVIDERS as CanonicalSectionType, label: 'Providers' },
  { entityType: ENTITY_SYNC_TYPES.COVERAGE as CanonicalSectionType, label: 'Coverage' },
];

const countSuggestedLinks = (pairs: EntityPairSchemaRead[]): number => {
  return pairs.reduce((count, pair) => {
    const fromPair = pair.link?.link_status === 'SUGGESTED' ? 1 : 0;
    const fromSuggestions = (pair.link_suggestions ?? []).filter(
      (s) => s.link_status === 'SUGGESTED',
    ).length;
    return count + fromPair + fromSuggestions;
  }, 0);
};

const countPendingSuggestions = (
  suggestionSets: FilevineEntitySyncSuggestionSetSchema[] = [],
  approvedEntityLinkIds?: Set<string>,
): number => {
  return (suggestionSets ?? []).reduce(
    (count, set) => {
      if (
        approvedEntityLinkIds &&
        (!set.entity_link_id || !approvedEntityLinkIds.has(set.entity_link_id))
      ) {
        return count;
      }
      return count + (set.suggestions ?? []).filter((s) => s.status === 'PENDING').length;
    },
    0,
  );
};

const EntitySyncPanel: React.FC<{
  matterId: string;
  firmId?: string;
  entityType: CanonicalSectionType;
  label: string;
}> = ({ matterId, firmId, entityType, label }) => {
  const {
    data: fieldConfig,
    isLoading: fieldConfigLoading,
    error: fieldConfigError,
    refetch: refetchFieldConfig,
  } = useEntitySyncFieldConfigQuery(
    { matterId, entityType },
    { skip: !matterId },
  );

  const {
    data: pairsData,
    isLoading: pairsLoading,
    error: pairsError,
    refetch: refetchPairs,
  } = useEntitySyncListPairsQuery(
    { matterId, entityType, includeUnlinked: true },
    { skip: !matterId },
  );

  const { data: suggestionsData } = useEntitySyncListDataSuggestionsQuery(
    { matterId, entityType },
    { skip: !matterId },
  );

  const pairs = useMemo(() => pairsData?.pairs ?? [], [pairsData?.pairs]);

  const items: DetailedFilevineCollectionItemSchema[] = useMemo(
    () =>
      pairs
        .filter((pair) => pair.filevine_entity != null)
        .map((pair) => pair.filevine_entity!),
    [pairs],
  );

  const finchEntities = useMemo(
    () =>
      pairs
        .filter((pair) => pair.finch_entity != null)
        .map((pair) => pair.finch_entity!),
    [pairs],
  );

  const suggestedLinkCount = useMemo(() => countSuggestedLinks(pairs), [pairs]);
  const approvedEntityLinkIds = useMemo(
    () =>
      new Set(
        pairs
          .filter((pair) => pair.link?.link_status === 'APPROVED' && !!pair.link?.id)
          .map((pair) => pair.link!.id),
      ),
    [pairs],
  );

  const pendingSuggestionCount = useMemo(
    () =>
      countPendingSuggestions(
        suggestionsData?.suggestion_sets,
        approvedEntityLinkIds,
      ),
    [suggestionsData?.suggestion_sets, approvedEntityLinkIds],
  );

  const hasActionableWork = suggestedLinkCount > 0 || pendingSuggestionCount > 0;

  const isNotConfigured =
    (fieldConfigError &&
      'status' in fieldConfigError &&
      fieldConfigError.status === 404) ||
    (pairsError && 'status' in pairsError && pairsError.status === 404);

  const [expanded, setExpanded] = useState(false);
  const [userToggled, setUserToggled] = useState(false);

  useEffect(() => {
    if (!userToggled && hasActionableWork) {
      setExpanded(true);
    }
  }, [hasActionableWork, userToggled]);

  const lastSyncAt = useMemo(() => {
    const latest = items.reduce<string | null>((currentLatest, item) => {
      const ts = item.latest_snapshot?.modified_at ?? null;
      if (!ts) return currentLatest;
      if (!currentLatest) return ts;
      return ts > currentLatest ? ts : currentLatest;
    }, null);

    if (!latest) return null;

    try {
      return formatDistanceToNow(parseISO(latest), { addSuffix: true });
    } catch {
      return null;
    }
  }, [items]);

  const isLoading = fieldConfigLoading || pairsLoading;

  const handleToggle = () => {
    setUserToggled(true);
    setExpanded((prev) => !prev);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={handleToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left hover:text-gray-700"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
            {items.length} item{items.length === 1 ? '' : 's'}
          </span>
          {suggestedLinkCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              {suggestedLinkCount} links
            </span>
          )}
        </button>

        <div className="ml-4 flex shrink-0 items-center gap-3">
          {lastSyncAt && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3.5 w-3.5" />
              Last synced {lastSyncAt}
            </span>
          )}
          <FullPipelineButton
            matterId={matterId}
            entityType={entityType}
            buttonSize="sm"
            className="px-3"
            onPipelineComplete={() => {
              refetchFieldConfig();
              refetchPairs();
            }}
          />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 px-4 py-4">
          {isLoading && (
            <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading sync data...</span>
            </div>
          )}

          {!isLoading && isNotConfigured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Filevine sync is not configured for this section.
            </div>
          )}

          {!isLoading && !isNotConfigured && (fieldConfigError || pairsError) && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <p className="mt-2 text-sm font-medium text-red-700">
                Failed to load sync data
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetchFieldConfig();
                  refetchPairs();
                }}
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !isNotConfigured && !fieldConfigError && !pairsError && (
            <div className="space-y-6">
              <EntityLinkingSection
                matterId={matterId}
                firmId={firmId}
                canonicalSectionType={entityType}
                fieldConfigs={fieldConfig?.field_configs || []}
                sectionConfigData={fieldConfig?.data}
                hideHeader
              />

              <PollingProvider>
                <SyncSuggestionCardList
                  matterId={matterId}
                  entityType={entityType}
                  canonicalSectionType={entityType}
                  firmId={firmId}
                  finchEntities={finchEntities}
                  filevineEntities={items}
                  fieldConfigs={fieldConfig?.field_configs || []}
                  sectionConfigData={fieldConfig?.data}
                  approvedEntityLinkIds={approvedEntityLinkIds}
                  includeReviewedAutoPushed
                  hideHeader
                />
              </PollingProvider>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EntitySyncAccordionSection: React.FC<{
  matterId: string;
  firmId?: string;
}> = ({ matterId, firmId }) => {
  if (!matterId) return null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Entity Sync</h2>
        <p className="mt-1 text-sm text-gray-500">
          Review linking and suggested updates for providers and coverage.
        </p>
      </div>

      {PANELS.map((panel) => (
        <EntitySyncPanel
          key={panel.entityType}
          matterId={matterId}
          firmId={firmId}
          entityType={panel.entityType}
          label={panel.label}
        />
      ))}
    </div>
  );
};

export default EntitySyncAccordionSection;
